import React, { useContext, useEffect, useRef, useState } from "react";
import { Dialog, Button, Flex, Text, RadioGroup, Spinner, Checkbox } from "@radix-ui/themes";
import { HelpCircle } from "lucide-react";
import { GlobalContext } from "../GlobalContext";
import { useToast } from "../ToastContext";
import exportSheetToExcel, { defaultExportName, hiddenColumnCount } from "../exportExcel";
import {
    fetchAllRows, applyClientFilters, canExportAll, MAX_EXPORT_ROWS,
} from "../exportAll";
import JoyridePortal from "../JoyridePortal";
import CustomTooltip from "../JoyrideCustomTooltip";
import useExportDialogTour from "../useExportDialogTour";
import { themeAccent } from "../theme";
import { CancelIcon, ICON_SIZE } from "../actionIcons";

/**
 * The download dialog for the data sheets.
 *
 * Replaces a window.prompt() for the filename, which blocked the main thread and
 * is a poor fit for the installed PWA, and adds the choice the sheets never
 * offered: this page, or every record matching the current filters.
 *
 * Sheets that don't paginate (Inventory, Committed, Credit) get no choice —
 * their one page already IS every record — so the scope picker is hidden and the
 * export is labelled accordingly.
 */
export default function ExportDialog({
    open, onOpenChange, pageRows, fieldOrder, sheetName, pagination, filterObject,
}) {
    const { DealershipID, userData, hiddenCostFields } = useContext(GlobalContext);
    const { showToast } = useToast();

    const [filename, setFilename] = useState(() => defaultExportName(sheetName));
    const [scope, setScope] = useState("page");
    const [includeHidden, setIncludeHidden] = useState(false);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const abortRef = useRef(null);

    //Started from in here, not from the header's help menu: a Radix dialog puts
    //an overlay over the whole page, so while this is open the header cannot be
    //clicked at all.
    const { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback } = useExportDialogTour();

    const paginated = canExportAll(sheetName);
    const totalRecords = pagination?.totalRecords ?? pageRows.length;
    const overCap = totalRecords > MAX_EXPORT_ROWS;
    const hiddenCount = hiddenColumnCount(fieldOrder);

    //Reset per opening — a stale filename or a half-finished progress line from
    //the previous export would be confusing.
    useEffect(() => {
        if (!open) return;
        setFilename(defaultExportName(sheetName));
        setScope("page");
        setIncludeHidden(false);
        setProgress(null);
        setBusy(false);
    }, [open, sheetName]);

    const close = () => { if (!busy) onOpenChange(false); };

    const cancel = () => {
        abortRef.current?.abort();
        setBusy(false);
        setProgress(null);
    };

    const run = async () => {
        const clean = filename.trim().replace(/[\\/:*?"<>|]/g, "-");
        if (!clean) return;

        if (scope === "page" || !paginated) {
            await exportSheetToExcel(pageRows, fieldOrder, sheetName, {
                filename: clean,
                scope: paginated ? "page" : "all",
                redactedFields: hiddenCostFields,
                includeHidden,
            });
            onOpenChange(false);
            return;
        }

        setBusy(true);
        setProgress({ fetched: 0, total: totalRecords });
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const rows = await fetchAllRows({
                sheetName,
                DealershipID,
                UserID: userData?.UserID,
                pagination,
                signal: controller.signal,
                onProgress: setProgress,
            });
            //The chips above the table filter the loaded page in the browser, so
            //without this "all records" would quietly ignore them.
            const filtered = applyClientFilters(rows, filterObject);
            if (!filtered.length) {
                showToast("error", "No records matched the current filters.", 5000);
                return;
            }
            await exportSheetToExcel(filtered, fieldOrder, sheetName, {
                filename: clean,
                scope: "all",
                redactedFields: hiddenCostFields,
                includeHidden,
            });
            onOpenChange(false);
        } catch (err) {
            if (err.name === "AbortError") return;      //user cancelled; already reset
            showToast("error", `Couldn't export: ${err.message}`, 6000);
        } finally {
            setBusy(false);
            setProgress(null);
            abortRef.current = null;
        }
    };

    return (
        <Dialog.Root open={open} onOpenChange={close}>
            <Dialog.Content style={{ maxWidth: 460 }}>
                <JoyridePortal
                    steps={steps}
                    stepIndex={stepIndex}
                    run={runTour}
                    callback={handleJoyrideCallback}
                    continuous
                    showProgress
                    showSkipButton
                    disableCloseOnEsc
                    disableOverlayClose
                    spotlightPadding={3}
                    locale={{ back: "Back", close: "Close", last: "Finish", next: "Next", skip: "Skip" }}
                    styles={{ options: { primaryColor: themeAccent(), zIndex: 10000 } }}
                    tooltipComponent={CustomTooltip}
                />
                <Dialog.Title>Download {sheetName}</Dialog.Title>

                {paginated ? (
                    <RadioGroup.Root value={scope} onValueChange={setScope} disabled={busy}>
                        <Flex direction="column" gap="2" mb="3">
                            <Text as="label" size="2">
                                <Flex gap="2" align="center">
                                    <RadioGroup.Item value="page" id="export-scope-page" />
                                    This page ({pageRows.length} row{pageRows.length === 1 ? "" : "s"})
                                </Flex>
                            </Text>
                            <Text as="label" size="2">
                                <Flex gap="2" align="center">
                                    <RadioGroup.Item value="all" id="export-scope-all" disabled={overCap} />
                                    All records ({totalRecords.toLocaleString()})
                                </Flex>
                            </Text>
                        </Flex>
                    </RadioGroup.Root>
                ) : (
                    <Text as="p" size="2" mb="3" color="gray">
                        This sheet loads every record, so the download covers all{" "}
                        {pageRows.length.toLocaleString()}.
                    </Text>
                )}

                {/* Naming the remedy beats disabling a control with no explanation. */}
                {paginated && overCap && (
                    <Text as="p" size="1" color="orange" mb="3" id="export-over-cap">
                        {totalRecords.toLocaleString()} records is above the {MAX_EXPORT_ROWS.toLocaleString()}-row
                        download limit. Narrow the date range in the footer, then try again.
                    </Text>
                )}

                {/* Stated because it is not recoverable from the file: the fetched
                    set comes back in server order, not the order on screen. */}
                {paginated && scope === "all" && !overCap && (
                    <Text as="p" size="1" color="gray" mb="3">
                        Uses the current search and date filters. Rows come in server order,
                        not the sort you have applied on screen.
                    </Text>
                )}

                {hiddenCount > 0 && (
                    <Text as="label" size="2" mb="3" style={{ display: "block" }}>
                        <Flex gap="2" align="center">
                            <Checkbox
                                id="export-include-hidden"
                                checked={includeHidden}
                                onCheckedChange={(v) => setIncludeHidden(v === true)}
                                disabled={busy}
                            />
                            Include the {hiddenCount} column{hiddenCount === 1 ? "" : "s"} hidden on screen
                        </Flex>
                    </Text>
                )}

                {/* Worth saying, because it is the non-obvious half: several
                    derived columns can only be formulas when their inputs are
                    in the file, and those inputs are hidden by default. */}
                {includeHidden && (
                    <Text as="p" size="1" color="gray" mb="3" id="export-hidden-note">
                        Also brings more columns to life as formulas, since their inputs
                        will be present.
                    </Text>
                )}

                <label>
                    <Text as="div" size="2" mb="1" weight="medium">File name</Text>
                    <input
                        id="export-filename"
                        className="input-t"
                        value={filename}
                        disabled={busy}
                        onChange={(e) => setFilename(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !busy) run(); }}
                    />
                </label>

                {progress && (
                    <Text as="p" size="2" mt="3" id="export-progress">
                        Fetched {progress.fetched.toLocaleString()} of {progress.total.toLocaleString()}…
                    </Text>
                )}

                <Flex gap="3" mt="4" justify="end">
                    {/* The tour is started from in here, not from the header's
                        help menu: a Radix dialog lays an overlay over the whole
                        page, so while this is open the header is unclickable. */}
                    <Button variant="ghost" color="gray" onClick={handleStartTutorial}
                        id="export-tour-button" title="Tour this dialog" disabled={busy} mr="auto">
                        <HelpCircle size={16} /> Tour
                    </Button>
                    {busy ? (
                        <Button variant="soft" color="gray" onClick={cancel} id="export-cancel">
                            <CancelIcon size={ICON_SIZE} />
                            Cancel
                        </Button>
                    ) : (
                        <Dialog.Close>
                            <Button variant="soft" color="gray"><CancelIcon size={ICON_SIZE} />Close</Button>
                        </Dialog.Close>
                    )}
                    <Button
                        onClick={run}
                        id="export-confirm"
                        disabled={busy || !filename.trim() || (scope === "all" && overCap)}
                    >
                        {busy && <Spinner size="1" />}
                        Download
                    </Button>
                </Flex>
            </Dialog.Content>
        </Dialog.Root>
    );
}
